import {
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { useGetFundraisingQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import { ErrorMessage, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getCrowdfundingFundraisingParameters } from '../../../utils';
import { crowdfundingSchema } from './schema';
import { ICrowdfundingFundraisingNaturalSubscriberInformations } from './types';

type Props = {
  defaultValues: ICrowdfundingFundraisingNaturalSubscriberInformations;
  onSubmit: (data: ICrowdfundingFundraisingNaturalSubscriberInformations) => void;
};

export function CrowdfundingNaturalSubscriberInformationsForm({
  defaultValues,
  onSubmit,
}: Props): JSX.Element {
  const i18nSubscriberInformationsForm = useTranslations('SubscriberInformationsForm');
  const i18nNaturalSubscriberInformationsForm = useTranslations(
    'NaturalSubscriberInformationsForm',
  );

  const { fundraisingId = '' } = useParams();
  const [hardCap, setHardCap] = useState<number>(0);

  const { register, formState, handleSubmit } =
    useForm<ICrowdfundingFundraisingNaturalSubscriberInformations>({
      defaultValues,
      resolver: yupResolver(crowdfundingSchema),
      context: { hardCap },
    });

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const parameters = useMemo(
    () => getCrowdfundingFundraisingParameters(data?.fundraising),
    [data?.fundraising],
  );

  useEffect(() => {
    setHardCap(parameters.hardCap || 0);
  }, [parameters]);

  if (loading) {
    //TODO: remove spinner
    return <Spinner />;
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  const handleFormSubmit: SubmitHandler<ICrowdfundingFundraisingNaturalSubscriberInformations> = (
    data,
  ): void => {
    onSubmit(data);
  };

  /*
  const handleFormChange = (e: React.ChangeEvent<HTMLFormElement>): void => {
    if (e.target.id === 'amount') {
      setIsAmountTouched(true);
    }
  };
  */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <FormControl isInvalid={!!formState.errors.email}>
            <FormLabel htmlFor="email">{i18nNaturalSubscriberInformationsForm('Email')}</FormLabel>
            <Input id="email" type="string" {...register('email')} />
            <ErrorMessage error={formState.errors.email} />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.firstName}>
              <FormLabel htmlFor="firstName">
                {i18nNaturalSubscriberInformationsForm('FirstName')}
              </FormLabel>
              <Input id="firstName" type="string" {...register('firstName')} />
              <ErrorMessage error={formState.errors.firstName} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.lastName}>
              <FormLabel htmlFor="lastName">
                {i18nNaturalSubscriberInformationsForm('LastName')}
              </FormLabel>
              <Input id="lastName" type="string" {...register('lastName')} />
              <ErrorMessage error={formState.errors.lastName} />
            </FormControl>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.minimalAmount}>
              <FormLabel htmlFor="minimalAmount">
                {i18nSubscriberInformationsForm('MinimalAmount')}
              </FormLabel>
              <Input
                id="minimalAmount"
                step="0.01"
                type="number"
                {...register('minimalAmount', { valueAsNumber: true })}
              />
              <Text color="gray.500" fontSize="xs">
                {i18nSubscriberInformationsForm('Default')}{' '}
                {formatNumberCurrency(parameters.minimalAmount || parameters.pricePerShare)}
              </Text>
              <ErrorMessage error={formState.errors.minimalAmount} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.maximalAmount}>
              <FormLabel htmlFor="maximalAmount">
                {i18nSubscriberInformationsForm('MaximalAmount')}
              </FormLabel>
              <Input
                id="maximalAmount"
                step="0.01"
                type="number"
                {...register('maximalAmount', { valueAsNumber: true })}
              />
              <Text color="gray.500" fontSize="xs">
                {i18nSubscriberInformationsForm('Default')}{' '}
                {formatNumberCurrency(parameters.maximalAmount || parameters.hardCap || 0)}
              </Text>
              <ErrorMessage error={formState.errors.maximalAmount} />
            </FormControl>
          </Stack>
          <FormControl isInvalid={!!formState.errors.admissionFees}>
            <FormLabel htmlFor="admissionFees">
              {i18nNaturalSubscriberInformationsForm('EntranceFee')}
            </FormLabel>
            <InputGroup>
              <Input id="admissionFees" step="0.01" type="number" {...register('admissionFees')} />
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.admissionFees} />
          </FormControl>
        </Stack>
        <Button data-cy="validate-subscriber" type="submit" w="full">
          {i18nNaturalSubscriberInformationsForm('AddSubscriber')}
        </Button>
      </Stack>
    </form>
  );
}

export type CrowdfundingNaturalSubscriberInformationsFormProps = Props;
